import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { showToast } from "../../../../core/utils/alert";
import { CourseFeeSharePayloadType } from "../../../fee-share/courseFeeShare/types/courseFeeShareRequest";
import { useCourseFeeShare } from "../../../fee-share/courseFeeShare/hooks/useCourseFeeShare";
import { useCourse } from "../../../configuration/course/hooks/useCourse";
import { useFeeType } from "../../../feetypes/hooks/useFeeType";
import { useCenter } from "../../../peoples/centers/hooks/useCenter";
import { useFeeConfig } from "../../../feeconfig/courseFeeConfig/hooks/useFeeConfig";
import CommonSelect from "../../../../core/common/commonSelect";

const CenterCourseFeeShare = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { id: centerId } = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const courseIdFromQuery = queryParams.get("course_id");
  const courseFeeIdFromQuery = queryParams.get("course_fee_id");
  const isEditMode = queryParams.get("edit") === "true";
  const shareIdFromQuery = queryParams.get("share_id");

  // Use the hooks to fetch data for dropdowns
  const { getCourseState } = useCourse();
  const { getFeeTypeState } = useFeeType();
  const { useGetCenterById } = useCenter();
  const {
    addFeeShare,
    updateFeeShare,
    isAdding,
    isUpdating,
    useGetFeeShareById
  } = useCourseFeeShare();
  const { useGetFeeConfigById } = useFeeConfig();

  // Get center data
  const { data: centerData } = useGetCenterById(centerId || "");

  // Get data from hooks
  const { courses, isLoading: isLoadingCourses } = getCourseState();
  const { feetypes, isLoading: isLoadingFeeTypes } = getFeeTypeState();

  // State to store fee config IDs
  const [courseFeeConfigId, setCourseFeeConfigId] = useState<string>("");

  // Get fee config data
  const { feeConfig: courseFeeConfig, loading: isLoadingCourseFeeConfig } =
    useGetFeeConfigById(courseFeeConfigId);

  // State to store course stages
  const [courseStages, setCourseStages] = useState<
    {
      stage_id: number;
      name: string;
      amount: number;
      shareAmount: number;
    }[]
  >([]);

  // Transform data for select components
  const courseOptions = courses.map((course) => ({
    value: course.id.toString(),
    label: course.name,
  }));

  // Get the first course fee type from feetypes
  const courseFeeType = feetypes.find(
    (feetype) => feetype.type === "coursefee"
  );

  // Fee type option
  const feeTypeOption = courseFeeType
    ? { value: courseFeeType.id.toString(), label: "Course Fee" }
    : null;

  const shareTypeOptions = [
    { value: "fixed", label: "Fixed Amount (₹)" },
    { value: "percent", label: "Percentage (%)" },
  ];

  // Initialize with empty values and set type_id to courseFeeType if available
  const [formData, setFormData] = useState<CourseFeeSharePayloadType>({
    course_id: 0,
    type_id: courseFeeType ? courseFeeType.id : 0,
    share_type: "",
    all_centers: false,
    center_ids: centerId ? [parseInt(centerId, 10)] : [],
    stageFeeShare: [],
  });

  const [selectedCourse, setSelectedCourse] = useState("");

  // Add state for edit mode
  const [isEdit, setIsEdit] = useState<boolean>(isEditMode);

  // Add hook to load existing fee share
  const { feeShare, loading: isLoadingFeeShare } = useGetFeeShareById(
    isEditMode && shareIdFromQuery ? shareIdFromQuery : ""
  );

  // Effect to set course fee type when component loads
  useEffect(() => {
    if (courseFeeType && formData.type_id === 0) {
      setFormData((prev) => ({
        ...prev,
        type_id: courseFeeType.id,
      }));
    }
  }, [feetypes, courseFeeType]);

  // Effect to ensure centerId is always in center_ids
  useEffect(() => {
    if (centerId) {
      const centerIdNum = parseInt(centerId, 10);
      if (!formData.center_ids?.includes(centerIdNum)) {
        setFormData(prev => ({
          ...prev,
          center_ids: [centerIdNum],
          all_centers: false
        }));
      }
    }
  }, [centerId, formData.center_ids]);

  // Effect to fetch fee configs when course are selected
  useEffect(() => {
    if (formData.course_id && courseFeeType) {
      // Set course fee config ID (using course_fee_id from query or course_id)
      // setCourseFeeConfigId(formData.course_id.toString());
      setCourseFeeConfigId(courseFeeIdFromQuery || "");
    } else {
      setCourseFeeConfigId("");
    }
  }, [formData.course_id, courseFeeType, courseFeeIdFromQuery]);

  // Effect to update course stages when course fee config is loaded
  useEffect(() => {
    if (courseFeeConfig?.coursefee && courseFeeConfig.coursefee.stageFees) {
      const stages = courseFeeConfig.coursefee.stageFees.map((stage) => {
        // Find the corresponding course stage to get its name
        const selectedCourse = courses.find(
          (course) => course.id === formData.course_id
        );

        // Find the stage in the course's stages
        const courseStage = selectedCourse?.courseStages?.find(
          (cs) => cs.id === stage.stage_id
        );

        // Use the actual stage name from the course data, or fallback to stage_name from the fee config or a generic name
        const stageName = courseStage?.name || stage.stage_name || `Stage ${stage.stage_id}`;

        return {
          stage_id: stage.stage_id,
          name: stageName,
          amount: stage.amount,
          shareAmount: 0, // Always start with 0 for new shares
        };
      });

      setCourseStages(stages);
    } else {
      setCourseStages([]);
    }
  }, [courseFeeConfig, courses, formData.course_id]);

  // Effect to set the course ID from query
  useEffect(() => {
    if (courseIdFromQuery) {
      setFormData((prev) => ({
        ...prev,
        course_id: parseInt(courseIdFromQuery, 10),
      }));
    }
  }, [courseIdFromQuery]);

  // Update the useEffect to correctly map the feeShare response data
  useEffect(() => {
    if (isEdit && feeShare) {
      const feeshareData = feeShare;

      setFormData(prev => ({
        ...prev,
        course_id: feeshareData.course?.id || 0,
        type_id: formData.type_id, // Keep existing type_id
        share_type: feeshareData.share_type || "",
        center_ids: centerId ? [parseInt(centerId, 10)] : [],
        all_centers: false,
        stageFeeShare: []
      }));

      // If we have stageFeeShare data, update the courseStages
      if (feeshareData.stageFeeShare && feeshareData.stageFeeShare.length > 0 && courseFeeConfig?.coursefee) {
        // Create a map for quick lookup of stage fees
        const stageFeeMap = new Map();
        feeshareData.stageFeeShare.forEach(stage => {
          stageFeeMap.set(stage.stage_id, {
            share_amount: stage.share_amount,
            stage_fee: stage.stage_fee
          });
        });

        // Update the courseStages with the share amounts from the API
        const updatedStages = courseFeeConfig.coursefee.stageFees.map(configStage => {
          const selectedCourse = courses.find(
            (course) => course.id === feeshareData.course?.id
          );

          const courseStage = selectedCourse?.courseStages?.find(
            (cs) => cs.id === configStage.stage_id
          );

          const stageName = courseStage?.name || configStage.stage_name || `Stage ${configStage.stage_id}`;

          // Find matching stage in the feeShare data
          const stageShare = stageFeeMap.get(configStage.stage_id);

          return {
            stage_id: configStage.stage_id,
            name: stageName,
            amount: stageShare?.stage_fee || configStage.amount,
            shareAmount: stageShare?.share_amount || 0
          };
        });

        setCourseStages(updatedStages);
      }
    }
  }, [isEdit, feeShare, courseFeeType, centerId, courses, courseFeeConfig]);

  // Add a separate effect to handle stage data specifically, which should run after the form data is set
  useEffect(() => {
    if (isEdit && feeShare?.stageFeeShare && courseFeeConfig?.coursefee) {
      const feeshareData = feeShare;

      // Create a lookup map for the share amounts
      const shareAmountMap = new Map();
      feeshareData?.stageFeeShare?.forEach(stage => {
        shareAmountMap.set(stage.stage_id, stage.share_amount);
      });

      // Update each stage with its share amount from the API
      setCourseStages(prevStages => {
        return prevStages.map(stage => {
          const shareAmount = shareAmountMap.get(stage.stage_id) || 0;
          return {
            ...stage,
            shareAmount
          };
        });
      });
    }
  }, [isEdit, feeShare, courseFeeConfig, courseStages.length]);

  const handleSelectChange = (fieldName: string, selected: any) => {
    if (selected) {
      setFormData((prev) => ({
        ...prev,
        [fieldName]:
          fieldName === "share_type"
            ? selected.value
            : parseInt(selected.value, 10),
      }));

      // If share type changes, we may need to update the course stage values
      if (fieldName === "share_type") {
        // Reset all stage share amounts if share type changes
        const newStages = [...courseStages].map((stage) => ({
          ...stage,
          shareAmount: 0,
        }));
        setCourseStages(newStages);
      }
    }
  };

  // Handle stage share amount change
  const handleStageShareAmountChange = (index: number, value: string) => {
    const parsedValue = parseFloat(value) || 0;
    let finalValue = parsedValue;
    const stageAmount = courseStages[index].amount;

    // If share type is percent, restrict to 0-100 range
    if (formData.share_type === "percent") {
      if (finalValue > 100) finalValue = 100;
      if (finalValue < 0) finalValue = 0;
    } else if (formData.share_type === "fixed") {
      if (finalValue > stageAmount) finalValue = stageAmount;
      if (finalValue < 0) finalValue = 0;
    }

    const newStages = [...courseStages];
    newStages[index].shareAmount = finalValue;
    setCourseStages(newStages);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.course_id) {
      showToast({
        title: "Error",
        text: "Please select a course",
        icon: "error",
      });
      return;
    }

    if (!formData.share_type) {
      showToast({
        title: "Error",
        text: "Please select a share type",
        icon: "error",
      });
      return;
    }

    // Validate that at least one stage has a share amount
    const hasStageWithShareAmount = courseStages.some(
      (stage) => stage.shareAmount > 0
    );
    if (!hasStageWithShareAmount && courseStages.length > 0) {
      showToast({
        title: "Error",
        text: "Please enter at least one stage share amount",
        icon: "error",
      });
      return;
    }

    try {
      // Create a map of stage IDs from the existing fee share data
      const existingStageShareMap = new Map();
      if (isEdit && feeShare?.stageFeeShare) {
        feeShare.stageFeeShare.forEach(stage => {
          existingStageShareMap.set(stage.stage_id, stage.id);
        });
      }

      // Create the payload, including stage IDs when updating
      const payload = {
        course_id: formData.course_id,
        type_id: formData.type_id,
        share_type: formData.share_type,
        center_id: parseInt(centerId || "0", 10),
        stageFeeShare: courseStages.map((stage) => {
          const stageData = {
            stage_id: stage.stage_id,
            share_amount: stage.shareAmount,
            stage_fee: stage.amount,
          };

          // If we're in edit mode and there's an existing ID for this stage, include it
          if (isEdit && existingStageShareMap.has(stage.stage_id)) {
            return {
              ...stageData,
              id: existingStageShareMap.get(stage.stage_id)
            };
          }

          return stageData;
        }),
      };

      let result;
      if (isEdit && shareIdFromQuery) {
        result = await updateFeeShare(shareIdFromQuery, payload);
      } else {
        result = await addFeeShare(payload);
      }

      if (result.success) {
        showToast({
          title: "Success",
          text: isEdit
            ? "Course fee share updated successfully"
            : "Course fee share created successfully",
          icon: "success",
        });
        navigate(-1);
      }
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.message || "An error occurred while creating fee share",
        icon: "error",
      });
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleCourseChange = (option: any | null) => {
    const courseValue = option?.value || "";
    setSelectedCourse(courseValue);
    // No need to reset dependent fields here since handleSelectChange will be called too
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">
                {isEdit ? "Edit" : "Add"} Course Fee Share for {centerData?.data?.center?.name || "Center"}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.centerList}>Centers</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={`/center-detail/${centerId}`}>Center Details</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Add Course Fee Share
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          {isEdit && isLoadingFeeShare ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading fee share data...</p>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-md-12">
                <form onSubmit={handleSubmit}>
                  {/* Fee Share Information Section */}
                  <div className="card mb-3">
                    <div className="card-header bg-light">
                      <div className="d-flex align-items-center">
                        <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                          <i className="ti ti-info-square-rounded fs-16" />
                        </span>
                        <h4 className="text-dark">Fee Share Information</h4>
                      </div>
                    </div>
                    <div className="card-body pb-1">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Course <span className="text-danger">*</span>
                            </label>
                            <CommonSelect
                              className="select"
                              options={courseOptions}
                              value={courseOptions.find(
                                (option) =>
                                  option.value === formData.course_id.toString()
                              )}
                              onChange={(selected) => {
                                handleCourseChange(selected);
                                handleSelectChange("course_id", selected);
                              }}
                              placeholder={
                                isLoadingCourses
                                  ? "Loading courses..."
                                  : "Select Course"
                              }
                              isDisabled={true}
                              required
                            />
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Fee Type <span className="text-danger">*</span>
                            </label>
                            <CommonSelect
                              className="select"
                              value={feeTypeOption || undefined}
                              options={feeTypeOption ? [feeTypeOption] : []}
                              isDisabled={true}
                              placeholder="Course Fee"
                            />
                          </div>
                        </div>

                        {/* Share Type field - Always show */}
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Share Type <span className="text-danger">*</span>
                            </label>
                            <CommonSelect
                              className="select"
                              options={shareTypeOptions}
                              value={shareTypeOptions.find(
                                (option) => option.value === formData.share_type
                              )}
                              onChange={(selected) =>
                                handleSelectChange("share_type", selected)
                              }
                              placeholder="Select Share Type"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Show Course Stage Fees section */}
                  {courseStages.length > 0 && (
                    <div className="card mb-3">
                      <div className="card-header bg-light">
                        <div className="d-flex align-items-center">
                          <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                            <i className="ti ti-list-details fs-16" />
                          </span>
                          <h4 className="text-dark">Course Stage Fees</h4>
                        </div>
                      </div>
                      <div className="card-body pb-1">
                        {isLoadingCourseFeeConfig ? (
                          <div className="text-center py-3">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="mt-2">Loading fee configuration...</p>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-bordered">
                              <thead className="table-light">
                                <tr>
                                  <th>Levels</th>
                                  <th>Fee Amount</th>
                                  <th>
                                    Share Amount (
                                    {formData.share_type === "fixed"
                                      ? "₹"
                                      : "%"}
                                    )
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {courseStages.map((stage, index) => (
                                  <tr key={stage.stage_id}>
                                    <td>{stage.name}</td>
                                    <td>₹{stage.amount.toLocaleString()}</td>
                                    <td>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder={`Enter share amount ${formData.share_type === "fixed"
                                            ? "(₹)"
                                            : "(%)"
                                          }`}
                                        value={stage.shareAmount}
                                        onChange={(e) =>
                                          handleStageShareAmountChange(
                                            index,
                                            e.target.value
                                          )
                                        }
                                        min="0"
                                        max={
                                          formData.share_type === "percent"
                                            ? 100
                                            : stage.amount
                                        }
                                        step={
                                          formData.share_type === "percent"
                                            ? "1"
                                            : "1"
                                        }
                                      />
                                      {formData.share_type === "percent" ? (
                                        <small className="text-muted">
                                          Max: 100%
                                        </small>
                                      ) : (
                                        <small className="text-muted">
                                          Max: ₹{stage.amount.toLocaleString()}
                                        </small>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="text-end mb-3">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isAdding || isUpdating}
                    >
                      {isAdding || isUpdating ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                          ></span>
                          {isEdit ? "Updating..." : "Saving..."}
                        </>
                      ) : (
                        isEdit ? "Update Course Fee Share" : "Save Course Fee Share"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CenterCourseFeeShare;
